'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense } from 'react';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import {
  AdditionalSettingsArticleList,
  BlockProps,
  ComponentBaseProps,
} from '@sola/schema/lib/schema.js';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.js';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.js';
import { compactObject } from '@sola/utils/object.js';

import { FeaturedArticleCard } from '../../../molecules/feature-article-card/feature-article-card';
import { useArticleQueryParams } from '../../../utils/query-params';

const queryClient = new QueryClient();

export type ArticleListProfoundProps = BlockProps & ComponentBaseProps;

const ArticleListProfoundSkeleton = () => (
  <section className="component">
    <div className="container">
      <div className="FeaturedArticleCard w-full max-w-xl mx-auto aspect-ratio-1 @md:aspect-ratio-16-9 @lg:aspect-ratio-21-9">
        <div className="FeaturedArticleCard__media">
          <Skeleton
            width="100%"
            height="100%"
            className="FeaturedArticleCard__image"
            borderRadius="var(--outline-radius-lg)"
          />
          <div className="FeaturedArticleCard__media-overlay" />
        </div>

        <div className="FeaturedArticleCard__content">
          <div className="max-w-md @lg:pr-roomy-lg">
            <div className="mb-comfy-md">
              <Skeleton height={20} width="90%" className="mb-snug-xs" />
              <Skeleton height={20} width="75%" />
            </div>

            <div className="flex flex-col gap-snug-xs">
              <Skeleton height={14} width="100%" />
              <Skeleton height={14} width="95%" />
              <Skeleton height={14} width="88%" />
              <Skeleton height={14} width="82%" />
            </div>
          </div>

          <div className="FeaturedArticleCard__meta flex items-start justify-between gap-comfy-xl flex-wrap pt-comfy-lg">
            <div className="flex flex-col gap-snug-lg">
              <Skeleton height={14} width={70} />
              <div className="flex flex-row items-center gap-snup-lg">
                <div className="pr-comfy-md">
                  <Skeleton height={32} width={32} borderRadius="50%" />
                </div>
                <Skeleton height={14} width={100} />
              </div>
            </div>

            <div className="flex flex-col gap-snug-lg">
              <Skeleton height={14} width={90} />
              <div className="mt-snug-lg">
                <Skeleton height={14} width={120} />
              </div>
            </div>

            <div className="flex flex-col gap-snug-lg sm:ml-auto text-left">
              <Skeleton height={14} width={50} />
              <div className="flex flex-wrap justify-end gap-snug-xl mt-snug-lg">
                <Skeleton height={20} width={75} borderRadius="9999px" />
                <Skeleton height={20} width={85} borderRadius="9999px" />
                <Skeleton height={20} width={65} borderRadius="9999px" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const ArticleListProfound: React.FC<ArticleListProfoundProps> = ({
  seoLevel = 3,
  settings,
}) => {
  const { articleFilters } = useArticleQueryParams();

  const articleListSettings = settings
    ? AdditionalSettingsArticleList.parse(settings)
    : undefined;

  const allFilters = {
    'taxonomy.articleTopics.keyword':
      articleListSettings?.topic || articleFilters.topic,
    'taxonomy.articleThemes.keyword':
      articleListSettings?.theme || articleFilters.theme,
    'taxonomy.articleCountry.keyword':
      articleListSettings?.country || articleFilters.country,
  };

  const compactFilters = compactObject(allFilters);

  const { data, isLoading } = useListArticlesQuery({
    must: JSON.stringify({
      'taxonomy.other.keyword': 'promotedContent',
      'taxonomy.articleType.keyword':
        articleListSettings?.articleType || undefined,
    }),
    filters: JSON.stringify(compactFilters),
    perPage: articleListSettings?.limit || 1,
    sort: 'datePublished',
    sortDir: 'desc',
  });

  if (isLoading) {
    return <ArticleListProfoundSkeleton />;
  }

  // @TODO: render as a gallery slider if limit > 1
  const article = data?.items?.[0];

  if (!article) {
    return null;
  }

  const cardProps = extractArgsSearchResult(article);

  return (
    <section className="component component--no-header">
      <div className="container">
        <FeaturedArticleCard {...cardProps} seoLevel={seoLevel} />
      </div>
    </section>
  );
};

export const ArticleListProfoundProvider: React.FC<BlockProps> = (props) => {
  return (
    <Suspense fallback={<ArticleListProfoundSkeleton />}>
      <QueryClientProvider client={queryClient}>
        <ArticleListProfound {...props} />
      </QueryClientProvider>
    </Suspense>
  );
};
